import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAdminCommand } from '@entities/withdrawal';
import type { AdminCommandDto } from '@shared/api/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shared/ui';

interface AdminCommandDialogProps {
  open: boolean;
  title: string;
  description?: string;
  busy?: boolean;
  confirmLabel?: string;
  reasonRequired?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (command: AdminCommandDto) => Promise<void>;
  children?: ReactNode;
}

export function AdminCommandDialog({
  open,
  title,
  description,
  busy = false,
  confirmLabel,
  reasonRequired = true,
  onOpenChange,
  onConfirm,
  children,
}: AdminCommandDialogProps) {
  const { t } = useTranslation('rbac');
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const pending = busy || submitting;

  useEffect(() => {
    if (!open) return;
    setReason('');
    setMethod('totp');
    setCode('');
    setFieldError(null);
    setSubmitting(false);
  }, [open]);

  const submit = async () => {
    if (!reasonRequired) {
      const factor = code.trim();
      if (!factor) {
        setFieldError(t('command.codeRequired'));
        return;
      }
      setFieldError(null);
      await runConfirm({ reason: reason.trim(), secondFactor: { method, code: factor } });
      return;
    }
    const command = buildAdminCommand(reason, method, code);
    if (!command.ok) {
      setFieldError(t(command.field === 'reason' ? 'command.reasonRequired' : 'command.codeRequired'));
      return;
    }
    setFieldError(null);
    await runConfirm({ reason: command.reason, secondFactor: command.secondFactor });
  };

  const runConfirm = async (command: AdminCommandDto) => {
    setSubmitting(true);
    try {
      await onConfirm(command);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="space-y-3">
          {children}
          <div className="space-y-2">
            <Label htmlFor="admin-command-reason">
              {reasonRequired ? t('command.reason') : t('command.reasonOptional')}
            </Label>
            <Textarea
              id="admin-command-reason"
              rows={3}
              value={reason}
              placeholder={t('command.reasonPlaceholder')}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="admin-command-method">{t('command.method')}</Label>
              <Select value={method} onValueChange={(value) => setMethod(value === 'backup' ? 'backup' : 'totp')}>
                <SelectTrigger id="admin-command-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="totp">{t('command.totp')}</SelectItem>
                  <SelectItem value="backup">{t('command.backup')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-command-code">{t('command.code')}</Label>
              <Input
                id="admin-command-code"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>
          </div>
          {fieldError ? <p className="text-sm text-destructive">{fieldError}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" disabled={pending} onClick={() => onOpenChange(false)}>
            {t('command.cancel')}
          </Button>
          <Button variant="default" loading={pending} onClick={() => void submit()}>
            {confirmLabel ?? t('command.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
